import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useStatementStore } from "../store/statementStore";

const BG_IMAGE =
    "https://static.prod-images.emergentagent.com/jobs/8719f201-69cb-44aa-b585-c2a8c70d5069/images/2aad4c3a11448e9ba6d9be5810c1b71f28c1bdc033fdadd0cae6456f2b28154c.png";

export default function UploadPage() {
    const navigate = useNavigate();
    const { upload, loading, error, uploadMessage, uploadProgress } = useStatementStore();
    const [localError, setLocalError] = useState(null);
    const [password, setPassword] = useState("");
    // Only shown when a selected PDF turns out to be password-protected.
    const [lockedFile, setLockedFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const tryUpload = useCallback(
        async (file, pwd) => {
            const data = await upload(file, pwd || null);
            navigate(`/workspace/${data.id}`);
        },
        [upload, navigate],
    );

    const onDrop = useCallback(
        async (files) => {
            const file = files[0];
            if (!file) return;
            setLocalError(null);
            setLockedFile(null);
            setPassword("");
            try {
                // Unlocked PDFs open directly; locked ones ask for a password.
                await tryUpload(file, null);
            } catch (e) {
                if (e.requiresPassword) {
                    setLockedFile(file);
                } else {
                    setLocalError(e.message);
                }
            }
        },
        [tryUpload],
    );

    const submitPassword = useCallback(
        async (e) => {
            e?.preventDefault?.();
            if (!lockedFile || !password) return;
            setLocalError(null);
            setSubmitting(true);
            try {
                await tryUpload(lockedFile, password);
            } catch (err) {
                setLocalError(
                    err.requiresPassword
                        ? "Incorrect password. Please try again."
                        : err.message,
                );
            } finally {
                setSubmitting(false);
            }
        },
        [lockedFile, password, tryUpload],
    );

    const cancelPassword = useCallback(() => {
        setLockedFile(null);
        setPassword("");
        setLocalError(null);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        multiple: false,
        disabled: Boolean(lockedFile),
    });

    return (
        <div className="relative min-h-screen w-full">
            {/* Background image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                    backgroundImage: `url(${BG_IMAGE})`,
                    opacity: 0.12,
                    filter: "blur(0px)",
                }}
            />
            <div className="absolute inset-0 dot-grid" style={{ opacity: 0.4 }} />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(1200px 600px at 50% -200px, rgba(59,130,246,0.10), transparent)",
                }}
            />

            {/* Top nav */}
            <header className="relative z-10 flex items-center justify-between px-8 py-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md bg-white text-black grid place-items-center font-mono text-[11px] font-semibold tracking-tight">
                        FE
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[13px] font-medium tracking-tight">
                            FinEdit Engine
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
                            Universal · Deterministic · Pixel-true
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="live-dot" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                        Engine Online
                    </span>
                </div>
            </header>

            <div className="relative z-10 max-w-[1100px] mx-auto px-8 pt-20 pb-10">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-12"
                >
                    <div className="flex items-center gap-2 mb-5 text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
                        <span className="px-2 py-1 border border-white/10 rounded">
                            v1 · Engine
                        </span>
                        <span className="px-2 py-1 border border-white/10 rounded">
                            PyMuPDF · pdf.js
                        </span>
                        <span className="px-2 py-1 border border-white/10 rounded text-emerald-400/80 border-emerald-500/30">
                            No reconstruction
                        </span>
                    </div>
                    <h1 className="text-[44px] leading-[1.05] font-medium tracking-tight max-w-[820px]">
                        Universal Financial PDF
                        <br />
                        <span className="text-zinc-500">Editing Engine.</span>
                    </h1>
                    <p className="mt-5 max-w-[640px] text-[15px] leading-[1.6] text-zinc-400">
                        Upload any bank statement. Edit a value, and every
                        downstream balance recomputes deterministically.
                        Export a PDF that preserves the{" "}
                        <span className="text-zinc-200">original font</span>,{" "}
                        <span className="text-zinc-200">size</span>, and{" "}
                        <span className="text-zinc-200">baseline</span> — no
                        layout reconstruction, no font drift.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                    {...getRootProps()}
                    className={`relative cursor-pointer glass rounded-2xl p-14 transition-colors ${
                        isDragActive
                            ? "border-blue-500/50 bg-blue-500/[0.04]"
                            : ""
                    }`}
                    data-testid="upload-dropzone"
                >
                    <input {...getInputProps()} data-testid="upload-input" />
                    <div className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 rounded-full border border-white/15 grid place-items-center mb-6">
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                className="text-zinc-300"
                            >
                                <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                            </svg>
                        </div>
                        <div className="text-[15px] font-medium tracking-tight">
                            {isDragActive
                                ? "Release to ingest statement"
                                : "Drop a bank statement PDF"}
                        </div>
                        <div className="mt-2 text-[12px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                            or click to browse · PDF only
                        </div>
                        <div className="mt-8 grid grid-cols-3 gap-3 w-full max-w-[680px]">
                            {[
                                ["Adaptive parser", "any layout · any bank"],
                                ["Typography preserved", "font · size · baseline"],
                                ["Deterministic recalc", "running balance ↻"],
                            ].map(([t, sub]) => (
                                <div
                                    key={t}
                                    className="px-4 py-3 rounded-md border border-white/[0.07] bg-black/30 text-left"
                                >
                                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-200">
                                        {t}
                                    </div>
                                    <div className="text-[10px] font-mono text-zinc-500 mt-1">
                                        {sub}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {lockedFile && (
                    <motion.form
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        onSubmit={submitPassword}
                        className="mt-6 glass rounded-2xl p-6 max-w-[520px] mx-auto"
                        data-testid="password-prompt"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                className="text-amber-400"
                            >
                                <rect x="4" y="11" width="16" height="9" rx="2" />
                                <path d="M8 11V8a4 4 0 018 0v3" />
                            </svg>
                            <span className="text-[14px] font-medium tracking-tight">
                                This PDF is password-protected
                            </span>
                        </div>
                        <p className="text-[12px] text-zinc-400 mb-4 truncate">
                            Enter the password for{" "}
                            <span className="text-zinc-200">{lockedFile.name}</span> to continue.
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                type="password"
                                autoFocus
                                placeholder="PDF password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="flex-1 bg-transparent border border-white/10 rounded px-3 py-2 text-sm focus:border-blue-500/50 outline-none"
                                data-testid="upload-password"
                            />
                            <button
                                type="submit"
                                disabled={!password || submitting}
                                className="px-4 py-2 rounded bg-white text-black text-sm font-medium disabled:opacity-40"
                                data-testid="password-submit"
                            >
                                {submitting ? "Unlocking…" : "Unlock"}
                            </button>
                            <button
                                type="button"
                                onClick={cancelPassword}
                                className="px-3 py-2 rounded border border-white/10 text-sm text-zinc-400"
                                data-testid="password-cancel"
                            >
                                Cancel
                            </button>
                        </div>
                    </motion.form>
                )}

                {(loading || error || localError) && (
                    <div className="mt-6 text-center">
                        {loading && (
                            <div className="text-[12px] font-mono text-zinc-400" data-testid="upload-loading">
                                <div className="mb-2">{uploadMessage || "Processing PDF"}</div>
                                <div className="mx-auto w-full max-w-[420px]">
                                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                        <span>Loading</span>
                                        <span>{Math.round(uploadProgress || 0)}%</span>
                                    </div>
                                    <div className="mt-2 h-2 rounded bg-white/10 overflow-hidden">
                                        <div
                                            className="h-full bg-emerald-400 transition-all duration-500"
                                            style={{ width: `${Math.max(0, Math.min(100, uploadProgress || 0))}%` }}
                                        />
                                    </div>
                                </div>
                                Parsing PDF · extracting spans · detecting columns…
                            </div>
                        )}
                        {(error || localError) && (
                            <div className="text-[12px] font-mono text-red-400" data-testid="upload-error">
                                {error || localError}
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-16 grid grid-cols-4 gap-4 text-[11px] font-mono text-zinc-500">
                    {[
                        ["01", "PARSE", "page.get_text('dict')"],
                        ["02", "MAP", "span → column → row"],
                        ["03", "RECALC", "balance += credit − debit"],
                        ["04", "EXPORT", "insert_text @ origin"],
                    ].map(([n, t, sub]) => (
                        <div key={n} className="border-l border-white/10 pl-3">
                            <div className="text-zinc-600">{n}</div>
                            <div className="text-zinc-200 mt-0.5">{t}</div>
                            <div className="text-zinc-500 mt-0.5 text-[10px]">
                                {sub}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
